import { inject, Injectable } from '@angular/core';

import { baseSortedMergeJoin } from '@blaze-shared/datasets-domain';
import { type RuntimeContext } from '@blaze-shared/elements-domain';
import { type Condition, IntelliSenseService } from '@blaze-shared/intellisense-feature';
import { sortByKey } from '@blaze-shared/platform-util';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { singularize } from '@blaze-shared/shared-util';
import { type JoinWorkflowNode } from '@blaze-shared/workflow-domain';

import { hasColumn } from '../../row-merge';

/** Restores the input's original order, which the sort-merge join necessarily destroys. */
const SORT_KEY = '__tempBlazeSortId';

/**
 * Joins the node's two inputs on the first condition it declares.
 *
 * Only the first condition drives the match — the rest are re-checked per candidate pair while
 * merging, which is what makes a multi-condition join work without a second pass. A node whose
 * condition parses to nothing joins nothing and passes input 1 through.
 */
@Injectable({ providedIn: 'root' })
export class JoinActionService {
  private readonly iss = inject(IntelliSenseService);
  private readonly schemaService = inject(SchemaService);

  join(
    node: JoinWorkflowNode,
    nodeInput1: any,
    nodeInput2: any,
    ctx: RuntimeContext,
    input2TableName: string,
  ): any {
    // Inputs may arrive as a single object, and the arrays are copied because joining sorts them.
    const sortableInput1 = Array.isArray(nodeInput1) ? [...nodeInput1] : [nodeInput1];
    const sortableInput2 = Array.isArray(nodeInput2) ? [...nodeInput2] : [nodeInput2];
    const expression = this.schemaService.replaceTagIdsWithValuesWhenString(node.condition) || '';
    const conditions = this.iss.createConditions(expression);

    const sortableInput1withId = sortableInput1.map((row, index) => ({
      ...row,
      [SORT_KEY]: index,
    }));

    if (!conditions?.length) {
      return nodeInput1;
    }
    const firstCondition: Condition = conditions[0];

    const mergedRows = baseSortedMergeJoin(
      sortableInput1withId,
      (rowA: any) =>
        this.iss.getNestedValueFromInput(
          rowA,
          firstCondition.input1.keys,
          firstCondition.input1.value,
        ),
      sortableInput2,
      (rowB: any) =>
        this.iss.getNestedValueFromInput(
          rowB,
          firstCondition.input2.keys,
          firstCondition.input2.value,
        ),
      (rowA: any, rowB: any) =>
        this.joinRows(rowA, rowB, input2TableName, ctx, expression, node.joinType),
      node.joinType,
    ).filter((row) => row !== null);

    return sortByKey(mergedRows, SORT_KEY).map(({ [SORT_KEY]: _sortId, ...row }) => row);
  }

  /**
   * Merges a matched pair of rows. Inner joins drop the pair when the full condition does not hold;
   * outer joins keep input 1's row.
   *
   * Columns unique to either side are copied across as they are; a name both rows carry keeps
   * input 1's value and takes input 2's under the target's singular name — `Product.Name` for a
   * `Name` that both a `Products` row and an `Orders` row carry.
   */
  private joinRows(
    rowA: any,
    rowB: any,
    rowBTableName: string,
    ctx: RuntimeContext,
    expression: string,
    joinType: JoinWorkflowNode['joinType'] = 'Inner',
  ): any {
    const merged = { ...rowA };
    const context = this.iss.updateRuntimeContext(ctx, rowA, rowB);
    if (!this.iss.eval(expression, context)) {
      return joinType === 'Inner' ? null : merged;
    }

    const colPrefix = singularize(rowBTableName);
    for (const col in rowB) {
      if (hasColumn(rowA, col) && hasColumn(rowB, col)) {
        merged[`${colPrefix}.${col}`] = rowB[col];
      } else if (!hasColumn(rowA, col)) {
        merged[col] = rowB[col];
      }
    }
    return merged;
  }
}
